import { useAuth } from '../../auth/context/AuthProvider';
import { toast } from 'sonner';

/**
 * Application-wide read-only gate for the user's currently active property
 * (`user.active_property_id` — the property picked in the navbar
 * PropertySelect, NOT any arbitrary property elsewhere in the app).
 *
 * `user.is_property_active` is `false` only when that specific property has
 * been deactivated via Property Management's Status switch (Channex
 * activate/deactivate feature). `null`/`undefined` (older backend, or user
 * with no active property yet) is treated as ACTIVE — fail-open, so a
 * missing field never silently locks staff out of the whole PMS.
 *
 * `user` updates live on property switch: `PropertySelect.tsx` refetches
 * `/v4/me` (via `useUser()`), which calls `login()` on every fetch,
 * re-rendering every consumer of this hook automatically — no extra
 * provider/query needed here.
 *
 * See IndexAi/property-management.md, "Read-only mode when property is
 * inactive" for the full list of gated modules and the reasoning behind
 * this mechanism.
 */
export const usePropertyReadOnly = () => {
    const { user } = useAuth();

    const isReadOnly = user?.is_property_active === false;
    const propertyName = user?.active_property ?? 'This property';
    const reason = `${propertyName} is currently inactive — this action is disabled until it's reactivated.`;

    /**
     * Wraps an imperative event handler (FullCalendar callbacks, dnd-kit
     * drag handlers, etc. — anywhere a JSX <ReadOnlyGuard> can't wrap the
     * trigger) so it becomes a no-op + a de-duplicated toast when read-only.
     */
    const guard = <T extends (...args: unknown[]) => unknown>(fn: T): T => {
        return ((...args: Parameters<T>) => {
            if (isReadOnly) {
                toast.error(reason, { id: 'property-inactive' });
                return undefined;
            }
            return fn(...args);
        }) as T;
    };

    return { isReadOnly, propertyName, reason, guard };
};
